'use client';

import { useState, useEffect } from 'react';
import MenuHero from './MenuHero';
import FilterBar from './FilterBar';
import MenuGrid from './MenuGrid';
import CustomizeModal from './CustomizeModal';

interface MenuItemData {
  id: number;
  name: string;
  description: string;
  price: number;
  image: string;
  alt: string;
  category: string;
  dietary: string[];
  spiceLevel: string;
  prepTime: number;
  isPopular: boolean;
  calories: number;
  protein: number;
  allergens: string[];
  ingredients: string[];
}

interface Addon {
  id: string;
  name: string;
  price: number;
  calories: number;
}

const MenuExperienceInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dietaryFilters, setDietaryFilters] = useState<string[]>([]);
  const [spiceLevel, setSpiceLevel] = useState('all');
  const [priceRange, setPriceRange] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [customizeItem, setCustomizeItem] = useState<MenuItemData | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const menuItems: MenuItemData[] = [
  {
    id: 1,
    name: 'Classic Disco Burger',
    description: 'Our signature sealed burger with premium beef patty, melted cheddar, crispy lettuce, tomatoes, and our secret disco sauce',
    price: 249,
    image: "https://images.unsplash.com/photo-1585508718415-6d83666de492",
    alt: 'Juicy beef burger with melted cheese, fresh lettuce and tomatoes on sesame seed bun',
    category: 'burgers',
    dietary: ['non-veg'],
    spiceLevel: 'mild',
    prepTime: 12,
    isPopular: true,
    calories: 650,
    protein: 35,
    allergens: ['Gluten', 'Dairy', 'Soy'],
    ingredients: ['Beef Patty', 'Cheddar Cheese', 'Lettuce', 'Tomato', 'Onion', 'Disco Sauce', 'Sesame Bun']
  },
  {
    id: 2,
    name: 'Spicy Neon Chicken',
    description: 'Fiery grilled chicken breast with jalapeños, pepper jack cheese, and our electric hot sauce that lights up your taste buds',
    price: 229,
    image: "https://images.unsplash.com/photo-1734773343286-99e5068b175b",
    alt: 'Grilled chicken burger with jalapeños and melted pepper jack cheese on toasted bun',
    category: 'burgers',
    dietary: ['non-veg'],
    spiceLevel: 'hot',
    prepTime: 15,
    isPopular: true,
    calories: 580,
    protein: 42,
    allergens: ['Gluten', 'Dairy'],
    ingredients: ['Chicken Breast', 'Pepper Jack Cheese', 'Jalapeños', 'Hot Sauce', 'Lettuce', 'Brioche Bun']
  },
  {
    id: 3,
    name: 'Veggie Groove Delight',
    description: 'Plant-based patty with avocado, caramelized onions, mushrooms, and tangy vegan mayo on a whole wheat bun',
    price: 199,
    image: "https://images.unsplash.com/photo-1543339462-88f4850abc5b",
    alt: 'Vegetarian burger with plant-based patty, fresh avocado slices and caramelized onions',
    category: 'burgers',
    dietary: ['veg', 'vegan'],
    spiceLevel: 'mild',
    prepTime: 10,
    isPopular: false,
    calories: 480,
    protein: 18,
    allergens: ['Gluten', 'Soy'],
    ingredients: ['Plant-Based Patty', 'Avocado', 'Mushrooms', 'Caramelized Onions', 'Vegan Mayo', 'Whole Wheat Bun']
  },
  {
    id: 4,
    name: 'Double Trouble Burger',
    description: 'Two juicy beef patties stacked high with double cheese, bacon, and our signature BBQ sauce for the ultimate indulgence',
    price: 349,
    image: "https://images.unsplash.com/photo-1734774204115-941fb31c25c8",
    alt: 'Double-decker beef burger with two patties, bacon strips and melted cheese layers',
    category: 'burgers',
    dietary: ['non-veg'],
    spiceLevel: 'medium',
    prepTime: 18,
    isPopular: true,
    calories: 920,
    protein: 58,
    allergens: ['Gluten', 'Dairy', 'Soy'],
    ingredients: ['Double Beef Patty', 'Cheddar Cheese', 'Bacon', 'BBQ Sauce', 'Pickles', 'Onion', 'Sesame Bun']
  },
  {
    id: 5,
    name: 'Paneer Tikka Fusion',
    description: 'Indian-inspired burger with spiced paneer tikka, mint chutney, onions, and crispy lettuce on a toasted bun',
    price: 219,
    image: "https://images.unsplash.com/photo-1547104550-386a4a8594bd",
    alt: 'Indian fusion burger with grilled paneer tikka pieces and fresh mint chutney',
    category: 'burgers',
    dietary: ['veg'],
    spiceLevel: 'medium',
    prepTime: 14,
    isPopular: true,
    calories: 520,
    protein: 24,
    allergens: ['Gluten', 'Dairy'],
    ingredients: ['Paneer Tikka', 'Mint Chutney', 'Onion', 'Lettuce', 'Tomato', 'Tandoori Bun']
  },
  {
    id: 6,
    name: 'Mushroom Swiss Melt',
    description: 'Sautéed mushrooms with melted Swiss cheese, caramelized onions, and garlic aioli on a pretzel bun',
    price: 239,
    image: "https://images.unsplash.com/photo-1549008531-3eefc84cba2d",
    alt: 'Gourmet burger with sautéed mushrooms, Swiss cheese and caramelized onions on pretzel bun',
    category: 'burgers',
    dietary: ['veg'],
    spiceLevel: 'mild',
    prepTime: 13,
    isPopular: false,
    calories: 590,
    protein: 28,
    allergens: ['Gluten', 'Dairy', 'Eggs'],
    ingredients: ['Beef Patty', 'Swiss Cheese', 'Mushrooms', 'Caramelized Onions', 'Garlic Aioli', 'Pretzel Bun']
  },
  {
    id: 7,
    name: 'Loaded Disco Fries',
    description: 'Crispy golden fries topped with melted cheese, bacon bits, jalapeños, and our signature disco sauce',
    price: 149,
    image: "https://images.unsplash.com/photo-1728691190487-92b12a0efc6b",
    alt: 'Golden crispy french fries loaded with melted cheese, bacon and jalapeño toppings',
    category: 'sides',
    dietary: ['non-veg'],
    spiceLevel: 'medium',
    prepTime: 8,
    isPopular: true,
    calories: 680,
    protein: 15,
    allergens: ['Dairy'],
    ingredients: ['French Fries', 'Cheddar Cheese', 'Bacon', 'Jalapeños', 'Disco Sauce', 'Green Onions']
  },
  {
    id: 8,
    name: 'Sweet Potato Wedges',
    description: 'Crispy sweet potato wedges seasoned with paprika and served with honey mustard dip',
    price: 129,
    image: "https://images.unsplash.com/photo-1564583412758-2d2a7a53f834",
    alt: 'Crispy sweet potato wedges with golden brown exterior and honey mustard dipping sauce',
    category: 'sides',
    dietary: ['veg', 'vegan', 'gluten-free'],
    spiceLevel: 'mild',
    prepTime: 10,
    isPopular: false,
    calories: 320,
    protein: 4,
    allergens: ['None'],
    ingredients: ['Sweet Potato', 'Paprika', 'Olive Oil', 'Honey Mustard Dip']
  },
  {
    id: 9,
    name: 'Onion Rings Tower',
    description: 'Crispy beer-battered onion rings stacked high and served with ranch dipping sauce',
    price: 119,
    image: "https://images.unsplash.com/photo-1696474551305-6b6e1c2af226",
    alt: 'Stack of crispy golden onion rings with ranch dipping sauce on the side',
    category: 'sides',
    dietary: ['veg'],
    spiceLevel: 'none',
    prepTime: 7,
    isPopular: true,
    calories: 450,
    protein: 8,
    allergens: ['Gluten', 'Dairy', 'Eggs'],
    ingredients: ['Onions', 'Beer Batter', 'Ranch Sauce']
  },
  {
    id: 10,
    name: 'Neon Blue Lemonade',
    description: 'Electric blue lemonade with a hint of butterfly pea flower, fresh lemon juice, and sparkling water',
    price: 89,
    image: "https://images.unsplash.com/photo-1656160333692-4d33fad5bbcb",
    alt: 'Vibrant blue lemonade drink in tall glass with ice cubes and lemon slice garnish',
    category: 'drinks',
    dietary: ['veg', 'vegan', 'gluten-free'],
    spiceLevel: 'none',
    prepTime: 3,
    isPopular: true,
    calories: 120,
    protein: 0,
    allergens: ['None'],
    ingredients: ['Butterfly Pea Flower', 'Lemon Juice', 'Sugar', 'Sparkling Water', 'Ice']
  },
  {
    id: 11,
    name: 'Mango Tango Shake',
    description: 'Creamy mango milkshake blended with vanilla ice cream and topped with whipped cream',
    price: 129,
    image: "https://images.unsplash.com/photo-1693857228079-d2e455c231f3",
    alt: 'Thick mango milkshake in glass topped with whipped cream and mango chunks',
    category: 'drinks',
    dietary: ['veg'],
    spiceLevel: 'none',
    prepTime: 5,
    isPopular: true,
    calories: 380,
    protein: 8,
    allergens: ['Dairy'],
    ingredients: ['Mango', 'Vanilla Ice Cream', 'Milk', 'Whipped Cream']
  },
  {
    id: 12,
    name: 'Classic Cola Float',
    description: 'Chilled cola with a scoop of vanilla ice cream creating the perfect fizzy dessert drink',
    price: 99,
    image: "https://images.unsplash.com/photo-1633983053996-ea3b277afbe5",
    alt: 'Cola float with vanilla ice cream scoop floating in fizzy cola drink',
    category: 'drinks',
    dietary: ['veg'],
    spiceLevel: 'none',
    prepTime: 2,
    isPopular: false,
    calories: 280,
    protein: 4,
    allergens: ['Dairy'],
    ingredients: ['Cola', 'Vanilla Ice Cream']
  },
  {
    id: 13,
    name: 'Chocolate Lava Brownie',
    description: 'Warm chocolate brownie with a molten center, served with vanilla ice cream and chocolate sauce',
    price: 159,
    image: "https://images.unsplash.com/photo-1606884285898-277317a7bf12",
    alt: 'Warm chocolate brownie with molten center oozing out, topped with vanilla ice cream',
    category: 'desserts',
    dietary: ['veg'],
    spiceLevel: 'none',
    prepTime: 6,
    isPopular: true,
    calories: 520,
    protein: 7,
    allergens: ['Gluten', 'Dairy', 'Eggs'],
    ingredients: ['Chocolate', 'Butter', 'Eggs', 'Flour', 'Sugar', 'Vanilla Ice Cream']
  },
  {
    id: 14,
    name: 'Strawberry Cheesecake',
    description: 'Creamy New York-style cheesecake with fresh strawberry compote and graham cracker crust',
    price: 179,
    image: "https://images.unsplash.com/photo-1560529952-3af231677fc8",
    alt: 'Slice of creamy cheesecake topped with fresh strawberries and strawberry sauce',
    category: 'desserts',
    dietary: ['veg'],
    spiceLevel: 'none',
    prepTime: 4,
    isPopular: true,
    calories: 450,
    protein: 9,
    allergens: ['Gluten', 'Dairy', 'Eggs'],
    ingredients: ['Cream Cheese', 'Graham Crackers', 'Strawberries', 'Sugar', 'Eggs', 'Vanilla']
  },
  {
    id: 15,
    name: 'Oreo Cookie Shake',
    description: 'Thick and creamy milkshake loaded with crushed Oreo cookies and topped with whipped cream',
    price: 149,
    image: "https://images.unsplash.com/photo-1638176066390-d1a2b56cc99c",
    alt: 'Thick Oreo milkshake with crushed cookie pieces, whipped cream and whole Oreo garnish',
    category: 'desserts',
    dietary: ['veg'],
    spiceLevel: 'none',
    prepTime: 5,
    isPopular: false,
    calories: 580,
    protein: 11,
    allergens: ['Gluten', 'Dairy', 'Soy'],
    ingredients: ['Oreo Cookies', 'Vanilla Ice Cream', 'Milk', 'Whipped Cream']
  }];


  const filterItems = () => {
    let filtered = menuItems;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter((item) => item.category === selectedCategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(
        (item) =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (dietaryFilters.length > 0) {
      filtered = filtered.filter((item) =>
      dietaryFilters.some((diet) => item.dietary.includes(diet))
      );
    }

    if (spiceLevel !== 'all') {
      filtered = filtered.filter((item) => item.spiceLevel === spiceLevel);
    }

    if (priceRange !== 'all') {
      if (priceRange === 'budget') {
        filtered = filtered.filter((item) => item.price < 200);
      } else if (priceRange === 'mid') {
        filtered = filtered.filter((item) => item.price >= 200 && item.price <= 400);
      } else if (priceRange === 'premium') {
        filtered = filtered.filter((item) => item.price > 400);
      }
    }

    if (sortBy === 'popular') {
      filtered = filtered.sort((a, b) => (b.isPopular ? 1 : 0) - (a.isPopular ? 1 : 0));
    } else if (sortBy === 'price-low') {
      filtered = filtered.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price-high') {
      filtered = filtered.sort((a, b) => b.price - a.price);
    } else if (sortBy === 'name') {
      filtered = filtered.sort((a, b) => a.name.localeCompare(b.name));
    }

    return filtered;
  };

  const handleAddToOrder = (
  item: MenuItemData,
  addons: Addon[],
  quantity: number,
  totalPrice: number) =>
  {
    console.log('Order added:', { item, addons, quantity, totalPrice });
    alert(`Added ${quantity}x ${item.name} to your order! Total: ₹${totalPrice}\n\nRedirecting to Instagram DM for order confirmation...`);
  };

  const filteredItems = isHydrated ? filterItems() : menuItems;

  return (
    <div data-component-id="src/app/menu-experience/components/MenuExperienceInteractive.tsx:367:4" data-component-path="src/app/menu-experience/components/MenuExperienceInteractive.tsx" data-component-line="367" data-component-file="MenuExperienceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <MenuHero data-component-id="src/app/menu-experience/components/MenuExperienceInteractive.tsx:368:6" data-component-path="src/app/menu-experience/components/MenuExperienceInteractive.tsx" data-component-line="368" data-component-file="MenuExperienceInteractive.tsx" data-component-name="MenuHero" data-component-content="%7B%22elementName%22%3A%22MenuHero%22%7D"
        onSearchChange={setSearchQuery}
        onCategorySelect={setSelectedCategory}
        selectedCategory={selectedCategory} />


      <FilterBar data-component-id="src/app/menu-experience/components/MenuExperienceInteractive.tsx:374:6" data-component-path="src/app/menu-experience/components/MenuExperienceInteractive.tsx" data-component-line="374" data-component-file="MenuExperienceInteractive.tsx" data-component-name="FilterBar" data-component-content="%7B%22elementName%22%3A%22FilterBar%22%7D"
        onDietaryChange={setDietaryFilters}
        onSpiceLevelChange={setSpiceLevel}
        onPriceRangeChange={setPriceRange}
        onSortChange={setSortBy} />


      <div data-component-id="src/app/menu-experience/components/MenuExperienceInteractive.tsx:381:6" data-component-path="src/app/menu-experience/components/MenuExperienceInteractive.tsx" data-component-line="381" data-component-file="MenuExperienceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%20py-12%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div data-component-id="src/app/menu-experience/components/MenuExperienceInteractive.tsx:382:8" data-component-path="src/app/menu-experience/components/MenuExperienceInteractive.tsx" data-component-line="382" data-component-file="MenuExperienceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-8%22%7D" className="flex items-center justify-between mb-8">
          <div data-component-id="src/app/menu-experience/components/MenuExperienceInteractive.tsx:383:10" data-component-path="src/app/menu-experience/components/MenuExperienceInteractive.tsx" data-component-line="383" data-component-file="MenuExperienceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
            <h2 data-component-id="src/app/menu-experience/components/MenuExperienceInteractive.tsx:384:12" data-component-path="src/app/menu-experience/components/MenuExperienceInteractive.tsx" data-component-line="384" data-component-file="MenuExperienceInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-2xl%20font-bold%20font-display%20text-textPrimary%22%7D" className="text-2xl font-bold font-display text-textPrimary">
              {selectedCategory === 'all' ? 'All Items' : selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)}
            </h2>
            <p data-component-id="src/app/menu-experience/components/MenuExperienceInteractive.tsx:387:12" data-component-path="src/app/menu-experience/components/MenuExperienceInteractive.tsx" data-component-line="387" data-component-file="MenuExperienceInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-textSecondary%20mt-1%22%2C%22textContent%22%3A%22items%20available%22%7D" className="text-textSecondary mt-1">{filteredItems.length} items available</p>
          </div>
        </div>

        <MenuGrid data-component-id="src/app/menu-experience/components/MenuExperienceInteractive.tsx:391:8" data-component-path="src/app/menu-experience/components/MenuExperienceInteractive.tsx" data-component-line="391" data-component-file="MenuExperienceInteractive.tsx" data-component-name="MenuGrid" data-component-content="%7B%22elementName%22%3A%22MenuGrid%22%7D" items={filteredItems} onCustomize={setCustomizeItem} />
      </div>

      {customizeItem &&
      <CustomizeModal data-component-id="src/app/menu-experience/components/MenuExperienceInteractive.tsx:395:6" data-component-path="src/app/menu-experience/components/MenuExperienceInteractive.tsx" data-component-line="395" data-component-file="MenuExperienceInteractive.tsx" data-component-name="CustomizeModal" data-component-content="%7B%22elementName%22%3A%22CustomizeModal%22%7D"
        item={customizeItem}
        onClose={() => setCustomizeItem(null)}
        onAddToOrder={handleAddToOrder} />

      }
    </div>);

};

export default MenuExperienceInteractive;